import React, { useState, useEffect } from 'react';
import { Button, Form, Row, Col, Container } from 'react-bootstrap';
import API from '../../api/baseUrl';
import { getTokens } from '../../auth/AuthState';
import { submitStyle, inputStyle, formStyle } from '../../auth/styles';
import moment from 'moment';
import { useForm, useMenuSettings } from '../hooks';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
// import sweetalert2 without inline styles
import Swal from 'sweetalert2/dist/sweetalert2.js';
// import styles separately
import 'sweetalert2/dist/sweetalert2.css';
import { Spinner } from 'react-bootstrap';
import { checkMedicare } from '../helpers/checkMedicare';

const formData = [];

function AddPatient(data) {
  const navigate = useNavigate();
  const { formState, onInputChange } = useForm(formData);
  const [validated, setValidated] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const { selectedTenancy } = useSelector((state) => state.clinicianProfile);
  const { allTenancyClinics } = useSelector((state) => state.clinicianProfile);
  const [medicareError] = useState(null);

  const { showAddNewPatient } = useMenuSettings();

  const clinics =
    allTenancyClinics?.filter(
      (clinic) => clinic.tenancy_id === selectedTenancy?.tenancy_id,
    ) || [];

  async function handleSubmit(e) {
    // if hide addNewPatient button, also avoid api call
    if (!showAddNewPatient) return;

    setSubmitting(true);
    const form = e.currentTarget;
    if (form.checkValidity() === false) {
      e.preventDefault();
      e.stopPropagation();
      setValidated(true);
      setSubmitting(false);
      return;
    } else {
      e.preventDefault();
      setValidated(true);
      const tokens = await getTokens();
      let options = {
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          Authorization: 'Bearer ' + tokens.id,
        },
      };
      let noPhone = false;

      if (
        !formState.phone ||
        formState.phone == null ||
        formState.phone == '' ||
        formState.phone.length == 0
      ) {
        //no phone number.. need to set a fakie
        formState.phone = '+378' + Math.random().toString().slice(2, 12);
        noPhone = true;
      } else if (formState.phone.startsWith('04')) {
        formState.phone = '+61' + formState.phone.slice(1);
        if (validateMobilPhoneAus(formState.phone) == false) {
          Swal.fire({
            icon: 'error',
            text: 'Invalid phone number.',
            showConfirmButton: true,
          });
          setSubmitting(false);
          return;
        }
      } else if (formState.phone.startsWith('+61')) {
        if (validateMobilPhoneAus(formState.phone) == false) {
          Swal.fire({
            icon: 'error',
            text: 'Invalid phone number.',
            showConfirmButton: true,
          });
          setSubmitting(false);
          return;
        }
      } else {
        Swal.fire({
          icon: 'error',
          text: 'Invalid phone number.',
          showConfirmButton: true,
        });

        setSubmitting(false);
        return;
      }

      if (!checkMedicare(formState.medicareNo)) {
        Swal.fire({
          icon: 'error',
          text: 'Invalid Medicare number.',
          showConfirmButton: true,
        });
        setSubmitting(false);
        if (formState.phone.startsWith('+378')) {
          formState.phone = null;
        }
        return;
      }

      const dob = moment(formState.dob).local();

      await API.post(
        `createPatient`,
        {
          username: formState.phone, // <--- username needs to correspond to phone number in Cognito.. Even if it's a fakie
          firstName: formState.firstName,
          middleName: formState.middleName,
          lastName: formState.lastName,
          dob: dob.format(),
          gender: formState.gender,
          medicare: formState.medicareNo,
          medicareIrn: Number.parseInt(formState.medicareIrn),
          postcode: formState.postcode,
          phone: noPhone ? null : formState.phone,
          primaryName: formState.pcName,
          primaryRelationship: formState.pcRelationship,
          primaryPhone: formState.pcPhone,
          primaryEmail: formState.pcEmail,
          clinics: [{ clinicId: Number.parseInt(formState.clinicRef) }],
          //todo later this will need to be adjustable
          tenancyId: selectedTenancy.tenancy_id,
        },
        options,
      )
        .then((res) => {
          setSubmitting(false);
          if (res.data.message == 'Success') {
            data.toggle();
            Swal.fire({
              icon: 'success',
              text: 'Patient creation success.',
              showConfirmButton: true,
              willClose(popup) {
                navigate(`/`, {});
                navigate(0);
              },
            });
          } else if (res.data.message == 'PatientExistsError') {
            Swal.fire({
              icon: 'error',
              text: 'Patient already exists.',
              showConfirmButton: true,
            });
            if (formState.phone.startsWith('+378')) {
              formState.phone = null;
            }
          } else if (res.data.message == 'PatientUpdated') {
            Swal.fire({
              icon: 'warning',
              text: 'Patient already exists and was added to the clinic',
              showConfirmButton: true,
            });
          } else {
            Swal.fire({
              icon: 'error',
              text: 'Error creating patient.',
              showConfirmButton: true,
            });
            if (formState.phone.startsWith('+378')) {
              formState.phone = null;
            }
          }
        })
        .catch((err) => {
          Swal.fire({
            icon: 'error',
            text: 'Error creating patient.',
            showConfirmButton: true,
          });
          if (formState.phone.startsWith('+378')) {
            formState.phone = null;
          }
          console.log(err);
          setSubmitting(false);
        });
    }
  }

  return (
    <div>
      {submitting ? (
        <Container style={formStyle} fluid>
          <div className="m-5 text-sm-center">
            <div>
              <Spinner animation="border" role="status"></Spinner>
              <span>
                <br />
                Submitting please wait...
              </span>
            </div>
          </div>
        </Container>
      ) : (
        <></>
      )}

      <Form
        style={formStyle}
        className={submitting ? 'submitting' : null}
        noValidate
        validated={validated}
        onSubmit={handleSubmit}
      >
        <div className="flexVertWrap text-lg-start pt-3">
          <b>New patient details</b>
        </div>

        <div className="flexVertWrap text-lg-start">
          <div className={'row'}>
            <Form.Group as={Col} md="4">
              <Form.Label>First name *</Form.Label>
              <Form.Control
                required
                label="First Name:"
                type="text"
                name="firstName"
                id="first-name"
                autoComplete="off"
                onChange={onInputChange}
              />
              <Form.Control.Feedback type="invalid">
                Please provide a first name
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group as={Col} md="4">
              <Form.Label>Middle name</Form.Label>
              <Form.Control
                label="Middle Name:"
                type="text"
                name="middleName"
                id="middle-name"
                autoComplete="off"
                onChange={onInputChange}
              />
              <Form.Control.Feedback type="invalid">
                Please provide a middle name
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group as={Col} md="4">
              <Form.Label>Last name *</Form.Label>
              <Form.Control
                required
                label="Last Name:"
                type="text"
                name="lastName"
                id="last-name"
                autoComplete="off"
                onChange={onInputChange}
              />
              <Form.Control.Feedback type="invalid">
                Please provide a last name
              </Form.Control.Feedback>
            </Form.Group>
          </div>

          <div className={'row mt-3'}>
            <Form.Group as={Col} md="4">
              <Form.Label>Date of birth *</Form.Label>
              <Form.Control
                required
                type="date"
                max={moment(new Date()).format('YYYY-MM-DD')}
                name="dob"
                id="dob"
                defaultValue={moment(new Date()).format('YYYY-MM-DD')}
                onChange={onInputChange}
              />
              <Form.Control.Feedback type="invalid">
                Please provide a valid date of birth
              </Form.Control.Feedback>
            </Form.Group>
            <Form.Group as={Col} md="4">
              <Form.Label>Gender *</Form.Label>
              <Form.Select
                required
                onChange={onInputChange}
                name="gender"
                id="gender"
                defaultValue=""
              >
                <option value="" disabled></option>
                <option key="F" value="F">
                  Female
                </option>
                <option key="I" value="I">
                  Intersex or indeterminate
                </option>
                <option key="M" value="M">
                  Male
                </option>
                <option key="S" value="S">
                  Not stated/inadequately described
                </option>
              </Form.Select>
              <Form.Control.Feedback type="invalid">
                Please select an option
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group as={Col} md="4">
              <Form.Label>Postcode *</Form.Label>
              <Form.Control
                as={'input'}
                required
                name="postcode"
                id="postcode"
                maxLength={4}
                minLength={4}
                type="text"
                autoComplete="off"
                onChange={onInputChange}
              />
              <Form.Control.Feedback type="invalid">
                Please provide a valid postcode
              </Form.Control.Feedback>
            </Form.Group>
          </div>

          <div className={'row mt-3'}>
            <Form.Group as={Col} md="4">
              <Form.Label>Medicare no *</Form.Label>
              <Form.Control
                as={'input'}
                required
                type="text"
                maxLength={10}
                minLength={10}
                name="medicareNo"
                autoComplete="off"
                id="medicareNo"
                onChange={onInputChange}
              />
              <Form.Control.Feedback type="invalid">
                Please provide a valid medicare number (10 digits)
              </Form.Control.Feedback>
              {medicareError && (
                <div className="invalid-feedback">{medicareError}</div>
              )}
            </Form.Group>

            <Form.Group as={Col} md="4">
              <Form.Label>Medicare IRN *</Form.Label>
              <Form.Control
                as={'input'}
                required
                type="text"
                name="medicareIrn"
                id="medicareIrn"
                autoComplete="off"
                maxLength={1}
                minLength={1}
                onChange={onInputChange}
              />
              <Form.Control.Feedback type="invalid">
                Please provide a valid medicare IRN
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group as={Col} md="4">
              <Form.Label>Service *</Form.Label>
              <Form.Select
                required
                onChange={onInputChange}
                name="clinicRef"
                id="clinicRef"
                defaultValue=""
                options={data.clinics}
              >
                <option value="" disabled></option>
                {clinics.map((clinic) => (
                  <option key={clinic.clinic_id} value={clinic.clinic_id}>
                    {clinic.clinic}
                  </option>
                ))}
              </Form.Select>
              <Form.Control.Feedback type="invalid">
                Please select a clinic
              </Form.Control.Feedback>
            </Form.Group>
          </div>

          <div className={'row mt-3'}>
            <Form.Group as={Col} md="4">
              <label>Phone No</label>
              <Form.Control
                type="tel"
                name="phone"
                autoComplete="off"
                maxLength={12}
                minLength={10}
                id="phone"
                onChange={onInputChange}
              />
              <Form.Control.Feedback type="invalid">
                Please provide a valid mobile phone number
              </Form.Control.Feedback>
            </Form.Group>

            <div className={'col'}></div>

            <div className={'col'}></div>
          </div>
        </div>

        <div className="flexVertWrap text-lg-start pt-3">
          <b>Primary contact details</b>
        </div>

        <div className="flexVertWrap text-lg-start">
          <div className={'row mt-2'}>
            <Form.Group as={Col} md="4">
              <label>Name</label>
              <Form.Control
                type="text"
                name="pcName"
                id="pcName"
                autoComplete="off"
                onChange={onInputChange}
              />
            </Form.Group>

            <Form.Group as={Col} md="4">
              <label>Relationship</label>
              <Form.Control
                type="text"
                name="pcRelationship"
                id="pcRelationship"
                onChange={onInputChange}
              />
            </Form.Group>

            <Form.Group as={Col} md="4">
              <label>Phone no</label>
              <Form.Control
                type="phone"
                name="pcPhone"
                autoComplete="off"
                id="pcPhone"
                onChange={onInputChange}
              />
            </Form.Group>
          </div>

          <div className={'row mt-3'}>
            <Form.Group as={Col} md="4">
              <label>Email address</label>
              <Form.Control
                type="email"
                name="pcEmail"
                id="pcEmail"
                autoComplete="off"
                onChange={onInputChange}
              />
            </Form.Group>

            <div className={'col'}></div>

            <div className={'col'}></div>
          </div>
        </div>

        <div>
          <button disabled={submitting} style={submitStyle} type="submit">
            Add
          </button>
        </div>
      </Form>
    </div>
  );
}

function validateMobilPhoneAus(phone) {
  if (phone) {
    //regex for australian mobile phone number starting in +61 followed by 9 digits
    const re = /^(\+61)\d{9}$/;
    return re.test(phone);
  } else {
    return false;
  }
}

export default AddPatient;
